import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import YouthImage from "../Assets/Youth.webp";
import WomenImage from "../Assets/WomenAndMothers.webp";
import SeniorsImage from "../Assets/Seniors.webp";
import FoodImage from "../Assets/FoodSecurity.webp";

const ease = [0.22, 1, 0.36, 1];

const revealUp = {
  hidden: {
    opacity: 0,
    y: 52,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.85,
      ease,
    },
  },
};

const revealLeft = {
  hidden: {
    opacity: 0,
    x: -50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease,
    },
  },
};

const revealRight = {
  hidden: {
    opacity: 0,
    x: 50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease,
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.13,
    },
  },
};

const programs = [
  {
    title: "Youth",
    desc: "Creating opportunities for young people to build skills, confidence, and stronger connections.",
    href: "/services",
    img: YouthImage,
  },
  {
    title: "Women & Mothers",
    desc: "Supporting women and mothers through community, care, and culturally informed programs.",
    href: "/services",
    img: WomenImage,
  },
  {
    title: "Seniors",
    desc: "Honouring the knowledge, experiences, and contributions of elders in our community.",
    href: "/services",
    img: SeniorsImage,
  },
  {
    title: "Food Security",
    desc: "Helping families access food support with dignity during difficult periods.",
    href: "/services",
    img: FoodImage,
  },
];

const Home = () => {
  return (
    <main className="-mt-10 min-h-screen bg-[#F3FBFA] dark:bg-[#141414] font-sans overflow-hidden transition-colors duration-300">
      {/* HERO */}
      <section className="relative w-full min-h-[94vh] md:min-h-[96vh] overflow-hidden bg-[#173F3E]">
        <motion.img
          initial={{
            scale: 1.08,
            opacity: 0.7,
          }}
          animate={{
            scale: 1,
            opacity: 1,
          }}
          transition={{
            duration: 1.8,
            ease,
          }}
          src="/images/478155672_653769543645028_3463871251763508755_n.jpg"
          alt="EthioCare community"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        {/* Balanced photograph overlay */}
        <div className="absolute inset-0 bg-black/25" />

        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-transparent" />

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_15%,rgba(0,0,0,0.25)_100%)]" />

        {/* Hero to page colour blend */}
        <div className="absolute bottom-0 left-0 right-0 z-[2] h-52 md:h-64 bg-gradient-to-b from-transparent via-[#F3FBFA]/35 to-[#F3FBFA] dark:via-[#141414]/40 dark:to-[#141414]" />

        <div className="relative z-10 min-h-[94vh] md:min-h-[96vh] flex items-center justify-center px-6 sm:px-10 lg:px-16">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="w-full max-w-6xl mx-auto text-center flex flex-col items-center pb-20 md:pb-28"
          >
            <motion.p
              variants={revealUp}
              className="mb-5 text-sm sm:text-base font-bold uppercase tracking-[0.3em] text-[#E8D34B]"
              style={{
                textShadow:
                  "0 2px 5px rgba(0,0,0,0.8), 0 5px 15px rgba(0,0,0,0.45)",
              }}
            >
              EthioCare
            </motion.p>

            <motion.h1
              variants={revealUp}
              className="max-w-6xl text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-extrabold leading-[1.02] tracking-[-0.045em]"
              style={{
                textShadow:
                  "0 3px 3px rgba(0,0,0,0.8), 0 7px 16px rgba(0,0,0,0.55), 0 18px 35px rgba(0,0,0,0.3)",
              }}
            >
              You Are Not Alone.
              <span className="block mt-3 text-[#E5CF3E]">
                Together, We Are Strong.
              </span>
            </motion.h1>

            <motion.p
              variants={revealUp}
              className="mt-8 max-w-3xl mx-auto text-lg sm:text-xl md:text-2xl text-white leading-relaxed font-semibold"
              style={{
                textShadow:
                  "0 2px 4px rgba(0,0,0,0.9), 0 5px 12px rgba(0,0,0,0.5)",
              }}
            >
              Supporting communities through culturally informed mental health
              awareness, connection, and care.
            </motion.p>

            <motion.div
              variants={revealUp}
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <a
                href="#programs"
                className="inline-flex min-h-[56px] min-w-[230px] items-center justify-center bg-[#D7C237] hover:bg-[#E4CF45] text-black px-8 py-4 text-base sm:text-lg font-bold transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:-translate-y-1"
              >
                Explore Our Programs
              </a>

              <Link
                to="/contact"
                className="inline-flex min-h-[56px] min-w-[230px] items-center justify-center border border-white/70 bg-black/20 hover:bg-white hover:text-[#163C3B] text-white px-8 py-4 text-base sm:text-lg font-bold backdrop-blur-[4px] transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.15)] hover:-translate-y-1"
              >
                Contact EthioCare
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="relative px-6 sm:px-10 lg:px-16 pb-24 md:pb-32 bg-[#F3FBFA] dark:bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.25,
            }}
            variants={revealUp}
            className="max-w-5xl"
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-6">
              Who We Are
            </p>

            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#163C3B] dark:text-white leading-[1.08] tracking-[-0.03em]"
              style={{
                textShadow:
                  "0 2px 3px rgba(17,63,61,0.14), 0 7px 18px rgba(17,63,61,0.05)",
              }}
            >
              Care shaped by culture,
              <br className="hidden sm:block" /> dignity, and community.
            </h2>

            <p className="mt-8 text-lg sm:text-xl md:text-2xl text-[#476564] dark:text-gray-300 leading-relaxed max-w-4xl">
              EthioCare was created following tragic losses connected to mental
              health challenges in our community. Our work helps families find
              understanding, support, and stronger pathways to care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VISION / MISSION / GOALS */}
      <section className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#EAF6F4] dark:bg-[#191919]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[0.75fr_1.25fr] gap-16 lg:gap-24 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.35,
              }}
              variants={revealLeft}
            >
              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-5">
                Our Purpose
              </p>

              <h2
                className="text-4xl sm:text-5xl font-extrabold text-[#163C3B] dark:text-white leading-tight tracking-[-0.03em]"
                style={{
                  textShadow:
                    "0 2px 3px rgba(17,63,61,0.13), 0 7px 18px rgba(17,63,61,0.05)",
                }}
              >
                Building a healthier, more connected community.
              </h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.2,
              }}
              variants={staggerContainer}
              className="border-t border-[#A7CBC6] dark:border-zinc-700"
            >
              <motion.div
                variants={revealRight}
                className="grid md:grid-cols-[150px_1fr] gap-4 md:gap-10 py-8 border-b border-[#A7CBC6] dark:border-zinc-700"
              >
                <h3 className="text-xl font-bold text-[#8B7D1C] dark:text-[#CDB937]">
                  Vision
                </h3>

                <p className="text-xl md:text-2xl text-[#365A58] dark:text-gray-300 leading-relaxed">
                  A community where everyone can live a full and happy life with
                  dignity.
                </p>
              </motion.div>

              <motion.div
                variants={revealRight}
                className="grid md:grid-cols-[150px_1fr] gap-4 md:gap-10 py-8 border-b border-[#A7CBC6] dark:border-zinc-700"
              >
                <h3 className="text-xl font-bold text-[#8B7D1C] dark:text-[#CDB937]">
                  Mission
                </h3>

                <p className="text-xl md:text-2xl text-[#365A58] dark:text-gray-300 leading-relaxed">
                  Reduce stigma, build awareness, and encourage help-seeking
                  across our community.
                </p>
              </motion.div>

              <motion.div
                variants={revealRight}
                className="grid md:grid-cols-[150px_1fr] gap-4 md:gap-10 py-8"
              >
                <h3 className="text-xl font-bold text-[#8B7D1C] dark:text-[#CDB937]">
                  Goals
                </h3>

                <div className="text-lg md:text-xl text-[#365A58] dark:text-gray-300 leading-relaxed space-y-3">
                  <p>Strengthen family and community wellness.</p>

                  <p>Connect families with support and resources.</p>

                  <p>Reduce barriers affecting long-term wellbeing.</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section
        id="programs"
        className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#F3FBFA] dark:bg-[#141414]"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            variants={revealUp}
            className="max-w-4xl mb-14 md:mb-20"
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-5">
              Our Programs
            </p>

            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#163C3B] dark:text-white leading-[1.05] tracking-[-0.03em]"
              style={{
                textShadow:
                  "0 2px 3px rgba(17,63,61,0.14), 0 7px 18px rgba(17,63,61,0.05)",
              }}
            >
              Support through every stage of community life.
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.1,
            }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {programs.map((program, index) => (
              <motion.article
                key={program.title}
                variants={revealUp}
                className="group"
              >
                <Link
                  to={program.href}
                  aria-label={`Learn more about ${program.title}`}
                  className="block h-full"
                >
                  <div className="relative h-full min-h-[520px] overflow-hidden rounded-[2rem] bg-[#E9F6F4] dark:bg-[#1C2423] border border-[#CEE4E0] dark:border-white/5 shadow-[0_18px_50px_rgba(32,82,78,0.08)] transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_25px_65px_rgba(32,82,78,0.15)]">
                    {/* IMAGE */}
                    <div className="relative h-[330px] sm:h-[370px] overflow-hidden">
                      <img
                        src={program.img}
                        alt={`${program.title} EthioCare program`}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045]"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-[#E9F6F4] via-transparent to-black/10 dark:from-[#1C2423]" />

                      <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-transparent" />

                      <p
                        className="absolute top-7 left-8 text-sm text-white font-bold tracking-[0.18em]"
                        style={{
                          textShadow: "0 2px 7px rgba(0,0,0,0.8)",
                        }}
                      >
                        0{index + 1}
                      </p>
                    </div>

                    {/* CONTENT */}
                    <div className="relative -mt-16 z-10 px-8 sm:px-10 pb-10">
                      <h3
                        className="text-3xl sm:text-4xl font-extrabold text-[#163C3B] dark:text-white"
                        style={{
                          textShadow: "0 2px 3px rgba(17,63,61,0.1)",
                        }}
                      >
                        {program.title}
                      </h3>

                      <p className="mt-5 text-lg text-[#4A6967] dark:text-gray-300 leading-relaxed max-w-xl">
                        {program.desc}
                      </p>

                      <p className="mt-7 text-sm uppercase tracking-[0.16em] font-bold text-[#887A18] dark:text-[#CDB937] transition-transform duration-300 group-hover:translate-x-2">
                        Learn More
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY QUOTE */}
      <section className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#EAF6F4] dark:bg-[#191919]">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.35,
          }}
          variants={revealUp}
          className="max-w-5xl mx-auto text-center"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-8">
            Community
          </p>

          <blockquote
            className="text-3xl sm:text-4xl md:text-5xl font-semibold text-[#163C3B] dark:text-white leading-[1.3] tracking-[-0.025em]"
            style={{
              textShadow:
                "0 2px 3px rgba(17,63,61,0.12), 0 7px 18px rgba(17,63,61,0.04)",
            }}
          >
            “EthioCare helped me through the hardest time in my life. I felt
            seen, heard, and supported.”
          </blockquote>

          <p className="mt-9 text-base font-semibold text-[#68817F] dark:text-gray-400">
            A community member
          </p>
        </motion.div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#163C3B] dark:bg-[#101010]">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.3,
          }}
          variants={revealUp}
          className="max-w-7xl mx-auto"
        >
          <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-20 items-end">
            <div className="max-w-4xl">
              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#D8C342] mb-6">
                Connect With Us
              </p>

              <h2
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.08] tracking-[-0.035em]"
                style={{
                  textShadow:
                    "0 3px 5px rgba(0,0,0,0.4), 0 10px 25px rgba(0,0,0,0.2)",
                }}
              >
                Need support or want to become part of our work?
              </h2>

              <p className="mt-7 text-lg sm:text-xl text-white/75 leading-relaxed max-w-3xl">
                Find support, connect with your community, or help EthioCare
                continue reaching families.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex min-h-[56px] items-center justify-center whitespace-nowrap bg-[#D1BD3D] hover:bg-[#E0CB4A] text-black px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
              >
                Contact EthioCare
              </Link>

              <Link
                to="/services"
                className="inline-flex min-h-[56px] items-center justify-center whitespace-nowrap border border-white/45 hover:border-white bg-transparent hover:bg-white hover:text-[#163C3B] text-white px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
              >
                View Services
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Home;